import { Timestamps, Versioned } from './shared.types';
import { Invoice } from './invoices.types';
import {
  ClaimStatus,
  MessageOperator,
  TopupDevice,
  TopupNumberMergeResult,
  TopupNumberStatus,
  TxSubtype,
  WalletTransferDirection,
} from './topups.types';

/**
 * Platform finance: the mobile-money ledger of the platform's own receiving
 * numbers (Top-up Numbers), matched to merchants/stores (billing), other
 * income, and expenses. Operator-neutral: works for any mobile-money operator.
 */

export enum PlatformFinanceCategory {
  /** Money in used to pay a store's invoices (subscription). */
  INC_SUBSCRIPTION = 'INC_SUBSCRIPTION',
  /** Money in credited to a merchant/store wallet (claimed top-up). */
  INC_TOPUP = 'INC_TOPUP',
  /** Money in for materials sold to a client. Credits the platform wallet. */
  INC_MATERIAL = 'INC_MATERIAL',
  /** Any other money in. Credits the platform wallet. */
  INC_OTHER = 'INC_OTHER',
  EXP_TRANSPORT = 'EXP_TRANSPORT',
  EXP_INTERNET = 'EXP_INTERNET',
  EXP_MATERIAL = 'EXP_MATERIAL',
  EXP_SALARY = 'EXP_SALARY',
  EXP_OTHER = 'EXP_OTHER',
  /** AI subscriptions and API credits (Claude, ChatGPT, DeepSeek). */
  EXP_AI = 'EXP_AI',
  /** Cloud hosting (AWS). */
  EXP_CLOUD = 'EXP_CLOUD',
  /** Software development tools (GitHub). */
  EXP_DEV_TOOLS = 'EXP_DEV_TOOLS',
  /**
   * Pays a founder back for business costs they paid personally. Settles
   * what the business owes; not counted again as an expense.
   */
  EXP_FOUNDER_REIMBURSEMENT = 'EXP_FOUNDER_REIMBURSEMENT',
}

export enum PlatformFinanceAssignmentStatus {
  ASSIGNED = 'ASSIGNED',
  UNASSIGNED = 'UNASSIGNED',
  /** Posting to wallets/billing failed; retry from the transaction. */
  FAILED = 'FAILED',
  /** The SMS could not be fully read (operator review needed). */
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

export enum PlatformFinanceAllocationStatus {
  PENDING = 'PENDING',
  POSTED = 'POSTED',
  FAILED = 'FAILED',
}

export interface PlatformFinanceSplit {
  /** Stable split id (ULID) — used as the posting idempotency suffix. */
  id: string;
  amount: number;
  category: PlatformFinanceCategory;
  /** Money in: the store the money is for. */
  store_id?: string;
  store_name?: string;
  /** Money out: the Sales Agent paid (optional; free-text recipient allowed). */
  employee_id?: string;
  recipient_name?: string;
  /** Set once the split was posted to wallets (idempotent retries skip it). */
  posted_at?: string;
  /** Wallet reference_id used for this split's posting. */
  posted_reference_id?: string;
}

/** An admin classification of one ledger transaction. */
export interface PlatformFinanceAllocation extends Versioned {
  receiver_number: string;
  transaction_id: string;
  direction: WalletTransferDirection.IN | WalletTransferDirection.OUT;
  /** Primary category (the category of the first split). */
  category: PlatformFinanceCategory;
  /** Money in: the single merchant the splits belong to. */
  merchant_id?: string;
  merchant_name?: string;
  splits: PlatformFinanceSplit[];
  note?: string;
  status: PlatformFinanceAllocationStatus;
  error?: string;
  /** INC_SUBSCRIPTION: invoices collected after the wallet credit. */
  collected_invoice_ids?: string[];
  /** The transaction's time (ISO UTC), copied for month grouping. */
  occurred_at: string;
  /** The transaction's amount (+ fee for money out), copied for reports. */
  total_amount: number;
  /** Optimistic-concurrency counter. */
  version: number;
  created_at: string;
  updated_at: string;
  updated_by_id?: string;
  updated_by_name?: string;
  /**
   * Set when the transaction moved to another account (a merged
   * needs-setup number): this copy is hidden. Optional for backwards
   * compatibility; missing = live.
   */
  deleted_at?: string;
  deleted_by_id?: string;
  deleted_by_name?: string;
  /** The account the allocation moved to. */
  moved_to?: string;
}

export interface PlatformFinanceAllocationDBItem
  extends PlatformFinanceAllocation {
  PK: string; // PLATFORM_FINANCE_ALLOCATION#<receiver_number>
  SK: string; // TX#<transaction_id>
  EntityIndexPK: string; // PLATFORM_FINANCE_ALLOCATION
  EntityIndexSK: string; // DATE#<occurred_at>#<transaction_id>
}

export enum PlatformFinancePhoneEntity {
  STORE = 'STORE',
  EMPLOYEE = 'EMPLOYEE',
}

export enum PlatformFinancePhoneStatus {
  LEARNING = 'LEARNING',
  SUGGEST = 'SUGGEST',
  TRUSTED = 'TRUSTED',
}

/** A learned link between a counterparty phone number and a store/agent. */
export interface PlatformFinancePhoneAssociation extends Versioned {
  phone_number: string;
  entity_type: PlatformFinancePhoneEntity;
  entity_id: string;
  merchant_id?: string;
  confirmations: number;
  conflicts: number;
  confidence: number;
  status: PlatformFinancePhoneStatus;
  first_seen_at: string;
  last_confirmed_at: string;
}

export interface PlatformFinancePhoneAssociationDBItem
  extends PlatformFinancePhoneAssociation {
  PK: string; // PLATFORM_FINANCE_PHONE#<phone_number>
  SK: string; // STORE#<id> | EMPLOYEE#<id>
  EntityIndexPK: string; // PLATFORM_FINANCE_PHONE
  EntityIndexSK: string; // PHONE#<phone_number>#<SK>
}

export enum PlatformFinanceWaiverReason {
  REFERRAL_REWARD = 'REFERRAL_REWARD',
  STORE_CLOSED = 'STORE_CLOSED',
  GOODWILL = 'GOODWILL',
  SPECIAL_AGREEMENT = 'SPECIAL_AGREEMENT',
  OTHER = 'OTHER',
}

export enum PlatformFinanceWaiverStatus {
  ACTIVE = 'ACTIVE',
  REMOVED = 'REMOVED',
}

/** A free month: the invoice is cancelled and the reason kept here. */
export interface PlatformFinanceWaiver extends Versioned {
  invoice_id: string;
  merchant_id: string;
  store_id: string;
  period_start: string;
  period_end: string;
  amount: number;
  reason: PlatformFinanceWaiverReason;
  note?: string;
  status: PlatformFinanceWaiverStatus;
  waived_by: string;
  waived_at: string;
  removed_by?: string;
  removed_at?: string;
  /** The invoice re-issued when the waiver was removed. */
  replacement_invoice_id?: string;
}

export interface PlatformFinanceWaiverDBItem extends PlatformFinanceWaiver {
  PK: string; // PLATFORM_FINANCE_WAIVER#<invoice_id>
  SK: string; // PLATFORM_FINANCE_WAIVER#<invoice_id>
  EntityIndexPK: string; // PLATFORM_FINANCE_WAIVER
  EntityIndexSK: string; // DATE#<waived_at>
  GSI1PK: string; // PLATFORM_FINANCE_WAIVER#STORE#<store_id>
  GSI1SK: string; // DATE#<period_start>
}

export enum PlatformFinanceAuditAction {
  ASSIGN_IN = 'ASSIGN_IN',
  ASSIGN_OUT = 'ASSIGN_OUT',
  WAIVE_INVOICE = 'WAIVE_INVOICE',
  REMOVE_WAIVER = 'REMOVE_WAIVER',
  REPLAY_SMS = 'REPLAY_SMS',
  /** A needs-setup (device) account merged into a Top-up Number. */
  MERGE_ACCOUNT = 'MERGE_ACCOUNT',
  /** A phone assigned to a Top-up Number (its held SMS replayed). */
  ASSIGN_DEVICE = 'ASSIGN_DEVICE',
}

export interface PlatformFinanceAuditEntry {
  id: string;
  action: PlatformFinanceAuditAction;
  record_type: string;
  record_id: string;
  old_value?: string;
  new_value?: string;
  reason?: string;
  actor_id: string;
  actor_name: string;
  created_at: string;
}

export interface PlatformFinanceAuditEntryDBItem
  extends PlatformFinanceAuditEntry {
  PK: string; // PLATFORM_FINANCE_AUDIT#<id>
  SK: string; // PLATFORM_FINANCE_AUDIT#<id>
  EntityIndexPK: string; // PLATFORM_FINANCE_AUDIT
  EntityIndexSK: string; // DATE#<created_at>#<id>
}

export enum PlatformFinanceReconciliationStatus {
  OK = 'OK',
  GAP = 'GAP',
  /** First transaction or missing balance: nothing to compare with. */
  REVIEW = 'REVIEW',
}

export interface PlatformFinanceSuggestion {
  category?: PlatformFinanceCategory;
  merchant_id?: string;
  store_id?: string;
  employee_id?: string;
  reason?: string;
}

/** A ledger row enriched with its classification (read model). */
export interface PlatformFinanceTransaction {
  transaction_id: string;
  receiver_number: string;
  direction: WalletTransferDirection.IN | WalletTransferDirection.OUT;
  operator?: MessageOperator;
  amount: number;
  fee: number;
  balance_after?: number;
  /** Inferred by reconciliation. */
  balance_before?: number;
  counterparty_number?: string;
  counterparty_name?: string;
  content?: string;
  message_content: string;
  occurred_at: string;
  subtype?: TxSubtype;
  reversed_by?: string;
  claim_status?: ClaimStatus;
  claimed_merchant_id?: string;
  claimed_store_id?: string;
  claimed_at?: string;
  assignment_status: PlatformFinanceAssignmentStatus;
  category?: PlatformFinanceCategory;
  /** Display label of who the money is from/for (store, merchant or recipient). */
  assigned_to?: string;
  allocation?: PlatformFinanceAllocation;
  suggestion?: PlatformFinanceSuggestion;
  reconciliation_status?: PlatformFinanceReconciliationStatus;
}

/** One Top-up Number (mobile-money account) with its SIM balance. */
export interface PlatformFinanceAccount {
  phone_number: string;
  label: string;
  operator?: MessageOperator;
  device_label?: string;
  status: TopupNumberStatus;
  platform_payment_type_id: string;
  balance?: number;
  balance_as_of?: string;
}

export interface PlatformFinanceDashboard {
  currency: string;
  /** Latest SIM balance of the selected account. */
  current_balance: number;
  balance_as_of?: string;
  month: string; // yyyy-MM (Africa/Maputo)
  money_in: number;
  subscription_income: number;
  /** Money in that is not subscription (top-ups, materials, other). */
  other_income: number;
  money_out: number;
  expenses: number;
  material_purchases: number;
  net: number;
  subscription_expected: number;
  subscription_received: number;
  subscription_outstanding: number;
  collection_pct: number;
  overdue_count: number;
  due_this_week: number;
  installation_count: number;
  non_active_count: number;
  unassigned_count: number;
  unassigned_amount: number;
  attention_count: number;
  /** Platform wallet this month: money paid to us. */
  platform_credits: number;
  /** Platform wallet this month: money we paid out. */
  platform_debits: number;
  recent: PlatformFinanceTransaction[];
}

export enum PlatformFinanceBillingStatus {
  PAYING = 'PAYING',
  INSTALLATION = 'INSTALLATION',
  NON_ACTIVE = 'NON_ACTIVE',
}

export interface PlatformFinanceClient {
  merchant_id: string;
  merchant_name: string;
  store_id: string;
  store_name: string;
  phone_number?: string;
  billing_status: PlatformFinanceBillingStatus;
  subscription_amount: number;
  /** Day of the month the store is billed on. */
  payment_day?: number;
  current_invoice_id?: string;
  current_invoice_number?: string;
  current_invoice_status?: string;
  current_due_date?: string;
  current_expected: number;
  current_paid: number;
  current_outstanding: number;
  current_waived: number;
  is_paid: boolean;
  is_overdue: boolean;
  is_waived: boolean;
  waiver_reason?: PlatformFinanceWaiverReason;
  waiver_note?: string;
  assigned_agent_name?: string;
}

export interface PlatformFinanceEmployee {
  id: string;
  name: string;
  phone_number?: string;
  cost_this_month: number;
}

export interface PlatformFinanceReportBucket {
  /** yyyy-MM (Africa/Maputo). */
  period: string;
  money_in: number;
  money_out: number;
  expenses: number;
  material_purchases: number;
  net: number;
}

export interface PlatformFinanceSystem {
  last_sms_at?: string;
  reconciliation_gaps: number;
  reconciliation_review: number;
  pending_setup_numbers: number;
  /** Phones whose SMS are held until they are assigned to a number. */
  unassigned_devices?: number;
}

export interface PlatformFinanceBootstrap {
  accounts: PlatformFinanceAccount[];
  /** The account the ledger data below belongs to. */
  receiver_number?: string;
  dashboard: PlatformFinanceDashboard;
  transactions: PlatformFinanceTransaction[];
  clients: PlatformFinanceClient[];
  employees: PlatformFinanceEmployee[];
  reports: PlatformFinanceReportBucket[];
  system: PlatformFinanceSystem;
  generated_at: string;
}

export interface GetPlatformFinanceBootstrap {
  /** Account to load; defaults to the first account. */
  receiver_number?: string;
}

export interface GetPlatformFinanceTransaction {
  receiver_number: string;
  transaction_id: string;
}

export interface PlatformFinanceIncomeSplitInput {
  store_id: string;
  amount: number;
}

export interface AssignPlatformFinanceIncome {
  receiver_number: string;
  transaction_id: string;
  category: PlatformFinanceCategory;
  /** Required for INC_SUBSCRIPTION / INC_TOPUP. */
  merchant_id?: string;
  /** INC_SUBSCRIPTION / INC_TOPUP: stores of `merchant_id`. May sum to less than the amount. */
  splits?: PlatformFinanceIncomeSplitInput[];
  note?: string;
  actor_id?: string;
  actor_name?: string;
}

export interface PlatformFinanceExpenseSplitInput {
  employee_id?: string;
  recipient_name?: string;
  category: PlatformFinanceCategory;
  amount: number;
}

export interface AssignPlatformFinanceExpense {
  receiver_number: string;
  transaction_id: string;
  splits: PlatformFinanceExpenseSplitInput[];
  note?: string;
  actor_id?: string;
  actor_name?: string;
}

export interface GetPlatformFinanceClient {
  merchant_id: string;
  store_id: string;
}

export interface PlatformFinanceClientDetail {
  client: PlatformFinanceClient;
  invoices: Invoice[];
  waivers: PlatformFinanceWaiver[];
  payments: PlatformFinanceTransaction[];
}

export interface WaivePlatformFinanceInvoice {
  invoice_id: string;
  reason: PlatformFinanceWaiverReason;
  note?: string;
  actor_id?: string;
  actor_name?: string;
}

export interface RemovePlatformFinanceWaiver {
  invoice_id: string;
  actor_id?: string;
  actor_name?: string;
}

export interface GetPlatformFinanceEmployee {
  employee_id: string;
  /** yyyy-MM; defaults to the current month. */
  month?: string;
}

export interface PlatformFinanceEmployeeLine {
  transaction_id: string;
  receiver_number: string;
  occurred_at: string;
  category: PlatformFinanceCategory;
  amount: number;
  note?: string;
}

export interface PlatformFinanceEmployeeDetail {
  employee: PlatformFinanceEmployee;
  totals: { category: PlatformFinanceCategory; amount: number }[];
  lines: PlatformFinanceEmployeeLine[];
}

export interface GetPlatformFinanceReconciliation {
  receiver_number: string;
}

export interface PlatformFinanceReconciliationGap {
  previous_transaction_id: string;
  current_transaction_id: string;
  previous_balance_after: number;
  current_balance_before: number;
  difference: number;
  missing_direction?: WalletTransferDirection.IN | WalletTransferDirection.OUT;
  missing_amount: number;
  explanation: string;
  previous?: PlatformFinanceTransaction;
  current?: PlatformFinanceTransaction;
}

export interface PlatformFinanceReconciliation {
  receiver_number: string;
  checked: number;
  gaps: PlatformFinanceReconciliationGap[];
}

export interface ReplayPlatformFinanceSms {
  start_date: string;
  end_date: string;
  /** Only the SMS of this phone (IFTTT device name). */
  device_name?: string;
  actor_id?: string;
  actor_name?: string;
}

/**
 * Merge a needs-setup (device) account into a Top-up Number: its
 * transactions, SMS and classifications move there. Preview by default.
 */
export interface MergePlatformFinanceAccount {
  /** The PENDING_SETUP `device:...` number. */
  from_phone_number: string;
  /** An existing Top-up Number. */
  to_phone_number: string;
  /** Preview only (the default); false applies it. */
  dry_run?: boolean;
  /** Also give the number the merged account's payment type. */
  use_from_payment_type?: boolean;
  actor_id?: string;
  actor_name?: string;
}

export interface PlatformFinanceAccountMergeResult {
  dry_run: boolean;
  topups: TopupNumberMergeResult;
  /** Classifications moved (or to move). */
  classifications: number;
  /**
   * Transactions the number already classifies differently: both
   * classifications are kept as they are, for review.
   */
  classification_conflicts: number;
  /** SMS the phone holds until it is assigned (processed by the merge). */
  held_sms: number;
  /** Held SMS of the phone re-published after the merge (0 in a preview). */
  replayed: number;
}

/** Assign a phone to a Top-up Number and process its held SMS. */
export interface AssignPlatformFinanceDevice {
  device_label: string;
  phone_number: string;
  actor_id?: string;
  actor_name?: string;
}

export interface PlatformFinanceDeviceAssignResult {
  device: TopupDevice;
  /** Held SMS re-published to be stored on the number. */
  replayed: number;
}

/** Stored IFTTT SMS re-published as IFTTT_SMS_RECEIVED (consumers are idempotent). */
export interface ReplayPlatformFinanceSmsResult {
  scanned: number;
  published: number;
}

export interface GetPlatformFinanceAuditLog {
  cursor?: string;
  limit?: number;
}

export interface PlatformFinanceAuditLogPage {
  items: PlatformFinanceAuditEntry[];
  cursor?: string;
  count: number;
}

// ---------------------------------------------------------------------------
// Business finances: founders, general expenses and manual income. These are
// paid or received outside the platform payment methods (e.g. a founder's
// personal card), so they never touch the platform wallet.
// ---------------------------------------------------------------------------

/** A founder who may pay business costs from a personal account. */
export interface PlatformFounder extends Versioned, Timestamps {
  id: string;
  name: string;
  /** Lower-case email addresses the founder's receipts come from. */
  emails: string[];
  created_by_id?: string;
  created_by_name?: string;
}

export interface PlatformFounderDBItem extends PlatformFounder {
  PK: string; // PLATFORM_FOUNDER#<id>
  SK: string; // identical to PK
  EntityIndexPK: string; // PLATFORM_FOUNDER
  EntityIndexSK: string; // DATE#<created_at>#<id>
}

export enum PlatformExpenseStatus {
  PAID = 'PAID',
  /** Billed or still accruing; not paid yet. */
  PENDING = 'PENDING',
  /** Expected later (a forecast or an upcoming renewal). */
  PROJECTED = 'PROJECTED',
  /** Read automatically; a founder has to check it before it counts. */
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

export enum PlatformExpenseSource {
  MANUAL = 'MANUAL',
  EMAIL = 'EMAIL',
  API = 'API',
}

/** A receipt or invoice file in the private receipts bucket. */
export interface PlatformFinanceReceipt {
  /** S3 object key. */
  key: string;
  file_name: string;
  content_type: string;
  /** Bytes. */
  size?: number;
}

/** A business cost paid outside the platform payment methods. */
export interface PlatformExpense extends Versioned, Timestamps {
  id: string;
  /** Who billed us, e.g. "AWS", "Anthropic", "GitHub". */
  provider: string;
  /** Which account or plan, e.g. "prod", "shopnsmile org", "Claude Max". */
  account?: string;
  description?: string;
  category: PlatformFinanceCategory;
  amount: number;
  /** ISO 4217 code of `amount`, e.g. USD. */
  currency: string;
  status: PlatformExpenseStatus;
  /** ISO UTC: when it was paid (PAID), billed (PENDING) or expected (PROJECTED). */
  occurred_at: string;
  /** ISO UTC: when a PENDING bill is due. */
  due_date?: string;
  source: PlatformExpenseSource;
  /** The founder who paid it personally; empty when the business paid. */
  paid_by_founder_id?: string;
  /** The founder's name when it was saved. */
  paid_by_name?: string;
  invoice_number?: string;
  receipt?: PlatformFinanceReceipt;
  /** ISO UTC: when the business paid the founder back. */
  reimbursed_at?: string;
  reimbursed_by_name?: string;
  note?: string;
  created_by_id?: string;
  created_by_name?: string;
}

export interface PlatformExpenseDBItem extends PlatformExpense {
  PK: string; // PLATFORM_EXPENSE#<id>
  SK: string; // identical to PK
  EntityIndexPK: string; // PLATFORM_EXPENSE
  EntityIndexSK: string; // DATE#<occurred_at>#<id>
}

/** Money the business received outside the mobile-money accounts. */
export interface PlatformIncome extends Versioned, Timestamps {
  id: string;
  /** Who paid us, e.g. a client, an investor or a founder. */
  source_name: string;
  description?: string;
  /** A money-in category other than INC_TOPUP. */
  category: PlatformFinanceCategory;
  amount: number;
  /** ISO 4217 code of `amount`. */
  currency: string;
  /** ISO UTC. */
  received_at: string;
  receipt?: PlatformFinanceReceipt;
  note?: string;
  created_by_id?: string;
  created_by_name?: string;
}

export interface PlatformIncomeDBItem extends PlatformIncome {
  PK: string; // PLATFORM_INCOME#<id>
  SK: string; // identical to PK
  EntityIndexPK: string; // PLATFORM_INCOME
  EntityIndexSK: string; // DATE#<received_at>#<id>
}

/** One category total. No category: not classified yet. */
export interface PlatformFinanceCategoryAmount {
  category?: PlatformFinanceCategory;
  amount: number;
}

/** Mobile-money totals of one month, across every Top-up Number. */
export interface PlatformMobileMoneyMonth {
  /** yyyy-MM (Africa/Maputo). */
  period: string;
  money_in: PlatformFinanceCategoryAmount[];
  /** Amount plus fee. */
  money_out: PlatformFinanceCategoryAmount[];
}

/** Everything the Business tab needs for one year, in one call. */
export interface PlatformBusinessFinance {
  year: number;
  /** Currency of the mobile-money amounts. */
  mobile_money_currency: string;
  mobile_money: PlatformMobileMoneyMonth[];
  /** True when a ledger read hit its row limit: totals may be too low. */
  mobile_money_capped: boolean;
  /** Top-up Numbers whose ledger could not be read (missing from totals). */
  mobile_money_errors: string[];
  founders: PlatformFounder[];
  /** General expenses of the year (any status). */
  expenses: PlatformExpense[];
  /** Founder-paid expenses of any year not paid back yet. */
  owed_expenses: PlatformExpense[];
  incomes: PlatformIncome[];
  generated_at: string;
}

export interface GetPlatformBusinessFinance {
  /** Calendar year; defaults to the current year. */
  year?: number;
}

export interface CreatePlatformFounder {
  name: string;
  emails?: string[];
  actor_id?: string;
  actor_name?: string;
}

/** Replaces the founder's editable fields. */
export interface UpdatePlatformFounder {
  id: string;
  /**
   * The version the form was opened at: a newer stored version is a 409.
   * Optional for backwards compatibility; missing = not checked.
   */
  version?: number;
  name: string;
  emails?: string[];
  actor_id?: string;
  actor_name?: string;
}

export interface DeletePlatformFounder {
  id: string;
  actor_id?: string;
  actor_name?: string;
}

export interface CreatePlatformExpense {
  provider: string;
  account?: string;
  description?: string;
  category: PlatformFinanceCategory;
  amount: number;
  currency: string;
  /** Defaults to PAID. */
  status?: PlatformExpenseStatus;
  occurred_at: string;
  due_date?: string;
  paid_by_founder_id?: string;
  invoice_number?: string;
  receipt?: PlatformFinanceReceipt;
  note?: string;
  actor_id?: string;
  actor_name?: string;
}

/** Replaces the expense's editable fields (a missing optional one is cleared). */
export interface UpdatePlatformExpense {
  id: string;
  /**
   * The version the form was opened at: a newer stored version is a 409.
   * Optional for backwards compatibility; missing = not checked.
   */
  version?: number;
  provider: string;
  account?: string;
  description?: string;
  category: PlatformFinanceCategory;
  amount: number;
  currency: string;
  status: PlatformExpenseStatus;
  occurred_at: string;
  due_date?: string;
  paid_by_founder_id?: string;
  invoice_number?: string;
  receipt?: PlatformFinanceReceipt;
  note?: string;
  actor_id?: string;
  actor_name?: string;
}

export interface DeletePlatformExpense {
  id: string;
  actor_id?: string;
  actor_name?: string;
}

/** Marks founder-paid expenses as paid back (or undoes it). */
export interface ReimbursePlatformExpenses {
  expense_ids: string[];
  /** False undoes the reimbursement. */
  reimbursed: boolean;
  actor_id?: string;
  actor_name?: string;
}

export interface ReimbursePlatformExpensesResult {
  expenses: PlatformExpense[];
}

export interface CreatePlatformIncome {
  source_name: string;
  description?: string;
  category: PlatformFinanceCategory;
  amount: number;
  currency: string;
  received_at: string;
  receipt?: PlatformFinanceReceipt;
  note?: string;
  actor_id?: string;
  actor_name?: string;
}

/** Replaces the income's editable fields (a missing optional one is cleared). */
export interface UpdatePlatformIncome {
  id: string;
  /**
   * The version the form was opened at: a newer stored version is a 409.
   * Optional for backwards compatibility; missing = not checked.
   */
  version?: number;
  source_name: string;
  description?: string;
  category: PlatformFinanceCategory;
  amount: number;
  currency: string;
  received_at: string;
  receipt?: PlatformFinanceReceipt;
  note?: string;
  actor_id?: string;
  actor_name?: string;
}

export interface DeletePlatformIncome {
  id: string;
  actor_id?: string;
  actor_name?: string;
}

/** Asks for a short-lived URL to upload one receipt (PDF or image). */
export interface CreatePlatformFinanceReceiptUpload {
  file_name: string;
  content_type: string;
  /** Bytes; at most 10 MB. */
  size: number;
}

export interface PlatformFinanceReceiptUpload {
  /** PUT the file here with the same Content-Type. */
  upload_url: string;
  /** Save this on the expense or income once the upload succeeded. */
  receipt: PlatformFinanceReceipt;
  expires_at: string;
}

export interface GetPlatformFinanceReceiptUrl {
  key: string;
}

export interface PlatformFinanceReceiptUrl {
  url: string;
  expires_at: string;
}
